import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { SlowMoversResponse } from '@/lib/api';
import { ChevronDown, ChevronRight, Loader2, PackageX, Search } from 'lucide-react';

function formatCurrency(amount: number | null) {
  if (amount === null) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
}

export default function SlowMovers() {
  const navigate = useNavigate();
  const [data, setData] = useState<SlowMoversResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleRow = (sku: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  };

  useEffect(() => {
    api.getSlowMovers()
      .then(setData)
      .catch((err) => console.error('Fehler beim Laden der Langsamdreher:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-ink-muted">Daten konnten nicht geladen werden.</p>;
  }

  const query = debouncedSearch.trim().toLowerCase();
  const filteredItems = data.items.filter((item) => {
    if (query.length < 2) return true;
    return (
      item.sku.toLowerCase().includes(query) ||
      item.name.toLowerCase().includes(query) ||
      (item.supplierName && item.supplierName.toLowerCase().includes(query))
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <PackageX className="h-8 w-8 text-brand" />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Überbestand</h1>
          <p className="text-[12.5px] text-ink-soft mt-1">
            Artikel mit überhöhtem Bestand und ohne aktive Verkäufe — gebundenes Kapital sichtbar machen.
          </p>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-card border border-line bg-surface flex items-center gap-4 px-5 py-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-panel bg-risk-yellow-soft flex items-center justify-center">
            <PackageX className="h-5 w-5 text-risk-yellow" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Kapitalbindung gesamt</p>
            <p className="text-2xl font-extrabold tracking-tight text-risk-yellow">{formatCurrency(data.summary.totalKapitalbindung)}</p>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface flex items-center gap-4 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Betroffene Artikel</p>
            <p className="text-2xl font-extrabold tracking-tight text-ink">{data.summary.count}</p>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface flex items-center gap-4 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Ø Reichweite</p>
            <p className="text-2xl font-extrabold tracking-tight text-ink">{data.summary.avgCoverageDays.toFixed(0)} Tage</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Langsamdreher</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
            <input
              type="text"
              placeholder="SKU, Name oder Lieferant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-9 w-64"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-3 text-left">
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Artikel</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Versandbestand</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Reichweite</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Letzte Aktivität</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">EK-Preis</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Kapitalbindung</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Lieferant</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-ink-soft font-mono text-[12.5px]">
                    {data.items.length === 0
                      ? 'Keine Langsamdreher identifiziert.'
                      : 'Keine Treffer für diese Suche.'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const hasBreakdown = !!item.warehouseBreakdown && item.warehouseBreakdown.length > 1;
                  const singleWarehouse =
                    item.warehouseBreakdown && item.warehouseBreakdown.length === 1
                      ? item.warehouseBreakdown[0]
                      : null;
                  const isExpanded = expandedRows.has(item.sku);

                  return (
                    <React.Fragment key={item.sku}>
                      <tr
                        onClick={() => navigate(`/products/${encodeURIComponent(item.sku)}`)}
                        className="cursor-pointer border-b border-surface-3 hover:bg-surface-2 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="text-[13.5px] font-bold text-ink">{item.name}</div>
                          <div className="font-mono text-[10.5px] text-ink-soft">{item.sku}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">
                          <span className="inline-flex items-center justify-end gap-1.5">
                            {hasBreakdown && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleRow(item.sku); }}
                                className="text-ink-soft hover:text-ink transition-colors"
                                aria-label={isExpanded ? 'Lager-Breakdown einklappen' : 'Lager-Breakdown ausklappen'}
                              >
                                {isExpanded
                                  ? <ChevronDown className="h-3.5 w-3.5" />
                                  : <ChevronRight className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            {item.onHand.toLocaleString('de-DE')}
                          </span>
                          {singleWarehouse && (
                            <div className="text-[10.5px] text-ink-soft">
                              {singleWarehouse.warehouseName === null
                                ? <span className="italic">Nicht zugeordnet</span>
                                : singleWarehouse.warehouseName}
                              {' · '}{singleWarehouse.onHand.toLocaleString('de-DE')} Stück
                              {' · '}{singleWarehouse.shippingWarehouse ? 'Versandlager' : 'Weiteres Lager'}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{item.coverageDays.toFixed(0)} Tage</td>
                        <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">
                          {item.daysSinceLastActivity !== null ? `vor ${item.daysSinceLastActivity} Tagen` : 'Nie'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[12.5px] text-ink">{formatCurrency(item.purchasePrice)}</td>
                        <td className="px-4 py-3 text-right font-mono text-[12.5px] font-bold text-risk-yellow">
                          {formatCurrency(item.kapitalbindung)}
                        </td>
                        <td className="px-4 py-3 text-ink-muted text-[13px]">{item.supplierName ?? '—'}</td>
                      </tr>

                      {/* Ausgeklappter Lager-Breakdown */}
                      {isExpanded && item.warehouseBreakdown && item.warehouseBreakdown.map((wh, idx) => (
                        <tr
                          key={`${item.sku}-wh-${wh.warehouseId ?? 'unassigned'}-${idx}`}
                          className="bg-surface-2 border-b border-surface-3"
                        >
                          <td className="px-4 py-2 font-mono text-[10.5px] text-ink-soft">
                            <div className="pl-5">
                              {wh.warehouseName === null
                                ? <span className="italic text-ink-soft">Nicht zugeordnet</span>
                                : wh.warehouseName}
                              <span className="ml-2">{wh.shippingWarehouse ? 'Versandlager' : 'Weiteres Lager'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-[12.5px] text-ink-muted">
                            {wh.onHand.toLocaleString('de-DE')}
                          </td>
                          <td colSpan={5} className="px-4 py-2"></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
